import { useState } from 'react';
import type { BoardroomReportBar } from '../../../../types';
import { SERIES_HUE } from '../Sparkline';

/**
 * How many rows fall in each group, as bars.
 *
 * Plain HTML rather than a chart library: a label, a bar and a number per line
 * reads at phone width, and the counts are right there to copy. Clicking a
 * line lists the rows in it, and each of those opens the item.
 *
 * With `chart: 'none'` it is the same list without the bars.
 */
export default function CountBlock({
  bars,
  chart = 'bar',
  onOpen,
}: {
  bars: BoardroomReportBar[];
  chart?: 'bar' | 'none';
  onOpen?: (id: number) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const most = Math.max(1, ...bars.map(b => b.count));

  if (!bars.length) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Nothing on the board matches this yet.
      </div>
    );
  }

  return (
    <ul className="space-y-1 text-xs" data-testid="count-block">
      {bars.map(bar => {
        const isOpen = open === bar.label;
        return (
          <li key={bar.label}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : bar.label)}
              aria-expanded={isOpen}
              className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <span className="w-36 shrink-0 truncate text-gray-700 dark:text-gray-200" title={bar.label}>
                {bar.label}
              </span>
              {chart === 'bar' && (
                <span className="min-w-0 flex-1">
                  <span
                    className={`block h-2.5 rounded-sm bg-current ${SERIES_HUE}`}
                    style={{ width: `${Math.max(2, (bar.count / most) * 100)}%` }}
                    data-testid="count-bar"
                  />
                </span>
              )}
              <span className="w-10 shrink-0 text-right tabular-nums text-gray-600 dark:text-gray-300">
                {bar.count}
              </span>
            </button>
            {isOpen && (
              <ul className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-200 pl-2 dark:border-gray-700">
                {bar.items.map(item => (
                  <li key={item.id}>
                    {onOpen ? (
                      <button
                        type="button"
                        onClick={() => onOpen(item.id)}
                        className="text-left text-gray-700 hover:underline dark:text-gray-200"
                      >
                        {item.title || `#${item.id}`}
                      </button>
                    ) : (
                      <span className="text-gray-700 dark:text-gray-200">{item.title}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
